import { trackEvent } from '@patrianna/analytics'
import { cleanDialogStack, closeDialogByName, getDialogStack, openDialog } from '@patrianna/dialog-manager'
import { getCookie } from '@patrianna/shared-utils'
import {
  listenPurchaseNotification,
  saveAndGetPaymentProviders,
} from '@patrianna-payments/shared-store/payments/store/actions'
import { offerDeclineNotificationHandler } from '@patrianna-payments/shared-store/payments/store/middleware-actions'
import { getQuickPurchaseSupportedTypeSelector } from '@patrianna-payments/shared-store/payments/store/selectors'
import { updateRedemptionList } from '@patrianna-payments/shared-store/redeem/store/actions'
import { setOTPMandatory, setSkipOTP, setSkipOtpClosed } from '@patrianna-payments/shared-store/skip-otp'
import { CLIENT_COORDINATES } from '@patrianna-ui-features/location-verification/src/constants'
import * as Sentry from '@sentry/nextjs'
import { batch } from 'react-redux'

import ROUTES from 'config/routes.json'
import {
  getRedemptionHistoryWithDateRage,
  redeemAuthorizedNotificationHandlerFirstTrustlyRedeem,
} from 'src/store/modules/redeem/actions'
import { getLocationPathname, getLocationSearch, navigateTo } from 'src/utils/browserLocation'
import { getPurchaseHistoryWithDateRage, trustlyAction } from 'store/middlewares/handlers/payments'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { logOut, logoutRequest } from 'store/modules/auth/actions'
import { saveBingoRoomsJackpots } from 'store/modules/bingo/actions'
import {
  updateInvitationBonusesNotificationListener,
  updateManualBonusesNotificationListener,
  setWheelFreeSpinsClaimState,
} from 'store/modules/bonusesFlow/actions'
import { isClaimWheelFreeSpinsInProgressSelector } from 'store/modules/bonusesFlow/selectors'
import { updateCurrenciesBalance } from 'store/modules/currencies/actions'
import { openGoldMineWinDialog, setJackpotOptIn, setMyStashWin } from 'store/modules/jackpots/actions'
import { setJackpotsData } from 'store/modules/jackpotsFromProviders/actions'
import { setKycStatus } from 'store/modules/kyc/actions'
import { prerequisitesChange } from 'store/modules/prerequisites/actions'
import { showFSModalFlow } from 'store/modules/slotGameFlow/actions'
import { openSnackbar } from 'store/modules/snackbar/actions'
import {
  getAccountInfoAndUpdateVerificationData,
  setIsEmailVerified,
  setLevelProgress,
  setLocationStatus,
  setSignUpFraudInfo,
  setUser,
  trackKYCConfirmedEvent,
} from 'store/modules/user/actions'

import type {
  JackpotWinNotification,
  NotifyResponses,
  RedeemAuthorizedNotification,
} from '@patrianna/shared-patrianna-types/websocket/notifications'
import type { PaymentProviders } from '@patrianna-payments/shared-store/payments/types/api/PaymentProviders'
import type { GetPaymentOrderResponseBody } from '@patrianna-payments/shared-store/payments/types/openApiModels/GetPaymentOrderResponseBody'
import type { OfferInfo } from '@patrianna-payments/shared-store/payments/types/openApiModels/OfferInfo'
import type { TypedThunk } from 'src/store/types'
import type { BingoRoomsResponseBody } from 'store/modules/bingo/types'
import type { ProviderJackpot, ProviderJackpotResponse } from 'store/modules/jackpotsFromProviders/types'

type JackpotsDTONotification = {
  type: 'JackpotsDTO'
  jackpots: Array<ProviderJackpotResponse>
}

type SignUpFraudCheckNotification = {
  type: 'SignUpFraudCheckNotification'
  geoVerificationRequired: boolean
}

type WsNotifyHandlerBody =
  | NotifyResponses
  | RedeemAuthorizedNotification
  | SignUpFraudCheckNotification
  | JackpotsDTONotification
  | BingoRoomsResponseBody

const jackpotKey = (currency: string, id: string) => `${currency}:${id}`

const wsNotifyHandler =
  ({ body }: { body: WsNotifyHandlerBody }): TypedThunk =>
  (dispatch, getState) => {
    batch(() => {
      switch (body.type) {
        case 'BalanceChangeNotification': {
          try {
            dispatch(updateCurrenciesBalance({ currency: body.balances }))
          } catch (error) {
            Sentry.captureMessage(`BalanceChangeNotification -  ${JSON.stringify(error)}`)
            Sentry.captureException(error)
          }

          break
        }

        case 'PrerequisitesChangeNotification': {
          dispatch(prerequisitesChange({ prerequisites: body.prerequisites }))
          break
        }

        case 'XPLevelChangeNotification': {
          dispatch(setLevelProgress(body))
          break
        }
        case 'VipLevelChangeNotification': {
          dispatch(setLevelProgress(body))
          break
        }

        case 'OfferPurchaseNotification': {
          const { isQuickPurchase, isQuickPurchaseGetCoins } = getQuickPurchaseSupportedTypeSelector(
            getState(),
            getLocationPathname(),
          )
          const isQuickPurchaseDialogOpen = !!getDialogStack()?.some(
            (dialog) => dialog.dialogName === 'QUICK_PURCHASE_DIALOG',
          )

          if (!(isQuickPurchase || isQuickPurchaseGetCoins || isQuickPurchaseDialogOpen)) {
            dispatch(listenPurchaseNotification(body as GetPaymentOrderResponseBody))
          }

          dispatch(getPurchaseHistoryWithDateRage())

          break
        }

        case 'SignOutNotification': {
          dispatch(logOut())

          break
        }

        case 'RedeemMoneyNotification': {
          // eslint-disable-next-line
          const { type, ...rest } = body
          // @ts-ignore todo add proper types
          dispatch(updateRedemptionList(rest))
          break
        }

        case 'RedeemAuthorizedNotification': {
          const { kyc, id, provider } = body

          dispatch(redeemAuthorizedNotificationHandlerFirstTrustlyRedeem(id, kyc, provider))

          dispatch(getRedemptionHistoryWithDateRage())

          break
        }

        case 'KYCDocSuccessUploadNotification': {
          closeDialogByName({ dialogName: 'VERIFICATION_KYC_DIALOG' })
          dispatch(setKycStatus({ status: 'doc_review' }))

          const query = new URLSearchParams(getLocationSearch())

          openDialog({
            dialogName: 'VERIFICATION_IN_PROGRESS_DIALOG',
            dialogProps: { verificationFrom: query?.get('verificationFrom') || '' },
          })
          break
        }

        case 'KYCDocErrorUploadNotification': {
          dispatch(
            openSnackbar({
              message: body.reason || 'Error uploading documents',
            }),
          )
          break
        }

        case 'KYCConfirmNotification': {
          dispatch(openSnackbar({ message: 'Id confirmed', variant: 'success' }))
          dispatch(trackKYCConfirmedEvent('ID_Verification_Success'))
          dispatch(getAccountInfoAndUpdateVerificationData('id_confirmed'))
          break
        }

        case 'KYCDocConfirmNotification': {
          dispatch(openSnackbar({ message: 'Document confirmed', variant: 'success' }))
          dispatch(trackKYCConfirmedEvent('POA_Verification_Success'))
          dispatch(getAccountInfoAndUpdateVerificationData('confirmed'))
          break
        }

        case 'KYCInReviewNotification': {
          dispatch(setKycStatus({ status: 'in_review' }))
          break
        }

        case 'KYCDocBlockNotification':
        case 'KYCBlockNotification': {
          dispatch(setKycStatus({ status: 'blocked' }))
          cleanDialogStack()

          // @TODO will change to home page
          dispatch(
            logoutRequest(
              `${ROUTES.HOME}?dialogName=LOCK_USER_DIALOG&dialogProps.reason=Your account has failed verification.`,
            ),
          )
          break
        }

        case 'ShowSystemMessageNotification': {
          if (body.markdown) {
            dispatch(
              openSnackbar({
                message: body.markdown,
              }),
            )
          }
          break
        }

        case 'AccountBlockNotification': {
          cleanDialogStack()

          if (body?.reloginProvider) {
            dispatch(
              logoutRequest(
                `${ROUTES.HOME}?dialogName=OTP_HELLO_AGAIN_DIALOG&dialogProps.reloginProvider=${body.reloginProvider}${
                  body?.reloginEmail ? `&dialogProps.reloginEmail=${body.reloginEmail}` : ''
                }`,
              ),
            )
          } else {
            dispatch(logoutRequest(`${ROUTES.HOME}?dialogName=LOCK_USER_DIALOG`))
          }
          break
        }

        case 'KYCDocDeclineNotification': {
          dispatch(setKycStatus({ status: 'doc_declined' }))
          break
        }

        case 'KYCDeclineNotification': {
          dispatch(setKycStatus({ status: 'declined' }))
          break
        }

        case 'AcceptManualBonusNotification': {
          // eslint-disable-next-line
          const { type, ...rest } = body
          dispatch(updateManualBonusesNotificationListener(rest))
          break
        }

        case 'AcceptInvitationBonusNotification': {
          // eslint-disable-next-line
          const { type, ...rest } = body
          dispatch(updateInvitationBonusesNotificationListener(rest))
          // backward compatibility
          if (rest?.account) {
            dispatch(setUser({ data: rest.account }))
          }
          break
        }

        case 'OfferRefundNotification': {
          dispatch(
            openSnackbar({
              message: 'Your purchase has been refunded: Please see email for more information.',
            }),
          )
          break
        }

        case 'InsufficientBalanceNotification': {
          // @TODO don't need this notification, because don't work for all game providers
          // dispatch(showLowOnCoinsDialog())
          break
        }

        case 'OfferDeclineNotification': {
          const { isQuickPurchase, isQuickPurchaseGetCoins } = getQuickPurchaseSupportedTypeSelector(
            getState(),
            window.location.pathname,
          )

          const isQuickPurchaseDialogOpen = !!getDialogStack()?.some(
            (dialog) => dialog.dialogName === 'QUICK_PURCHASE_DIALOG',
          )

          if (!(isQuickPurchase || isQuickPurchaseGetCoins || isQuickPurchaseDialogOpen)) {
            dispatch(offerDeclineNotificationHandler(body))
          }

          break
        }

        case 'PurchaseRefreshNotification': {
          const { tempToken, transactionId, offer, code, provider } = body

          // This flow will replace errCode === 'expired' logic
          if (provider === 'trustly') {
            dispatch(trustlyAction({ tempToken, transactionId, offer: offer as OfferInfo, code }))
          }

          break
        }

        case 'EmailVerifiedNotification': {
          trackEvent('email_verification_completed', {
            interaction_name: 'email_verification_completed',
          })
          dispatch(setIsEmailVerified({ isVerified: true }))
          navigateTo(ROUTES.HOME)
          break
        }

        case 'SkipOtpChangeNotification': {
          dispatch(setSkipOTP({ skipOtp: body?.skipOtp }))
          dispatch(setOTPMandatory({ mandatory: body?.mandatory }))
          dispatch(setSkipOtpClosed({ closed: false }))
          break
        }

        case 'JackpotOptOutNotification': {
          dispatch(setJackpotOptIn({ optIn: false, gcContributeAmount: '50', auto: body.auto }))
          break
        }

        case 'JackpotWinNotification': {
          const { amount, jackpotId, subtype } = body as JackpotWinNotification

          if (subtype === 'MY_STASH') {
            dispatch(setMyStashWin({ amount, jackpotId }))
          }

          if (body.subtype === 'GOLD_MINE') {
            dispatch(openGoldMineWinDialog(amount))
          }
          break
        }

        case 'SignUpFraudCheckNotification': {
          dispatch(
            setSignUpFraudInfo({
              fraudCheckCompleted: true,
              geoVerificationRequired: body?.geoVerificationRequired ?? false,
            }),
          )

          if (body?.geoVerificationRequired && !getCookie(CLIENT_COORDINATES, document.cookie)) {
            dispatch(setLocationStatus({ locationStatus: 'FORCE_GPS' }))
          }

          break
        }

        case 'UpdatePaymentMethodsNotification': {
          const { withdrawProviders, purchaseProviders } = body as unknown as PaymentProviders

          dispatch(saveAndGetPaymentProviders({ withdrawProviders, purchaseProviders }))

          break
        }

        case 'CreateFreeSpinNotification': {
          const isClaimWheelFreeSpinsInProgress = isClaimWheelFreeSpinsInProgressSelector(getState())

          // Wheel of Winners: Only set the flag if claim free spins is in progress (from start until claim click)
          if (isClaimWheelFreeSpinsInProgress) {
            dispatch(setWheelFreeSpinsClaimState('received'))
          }

          // Don't show free spins modal if wheel claim is in progress
          if (!isClaimWheelFreeSpinsInProgress) {
            dispatch(showFSModalFlow(body.freeSpins))
          }

          break
        }

        case 'JackpotsDTO': {
          let hasFresherData = false
          const isScEnabled = sweepstakeEnabledSelector(getState())
          const currentCurrency = isScEnabled ? 'SC' : 'GC'
          const jackpotsByKey = new Map<string, ProviderJackpot>(
            (getState().providersJackpots?.jackpots ?? []).map((jp) => [jackpotKey(jp.currency, jp.id), jp]),
          )

          for (const jackpot of body.jackpots ?? []) {
            if (jackpot.currency !== currentCurrency) {
              continue
            }

            const incomingTimestamp = Number(jackpot.timestamp) || 0
            const key = jackpotKey(jackpot.currency, jackpot.id)
            const previous = jackpotsByKey.get(key)

            if (
              previous &&
              incomingTimestamp &&
              typeof previous.timestamp === 'number' &&
              incomingTimestamp <= previous.timestamp
            ) {
              continue
            }

            jackpotsByKey.set(key, {
              id: jackpot.id,
              name: jackpot.name,
              status: '',
              level: '',
              currency: jackpot.currency,
              amount: Number(jackpot.amount),
              supplier: jackpot.supplier,
              timestamp: jackpot.timestamp,
            })

            hasFresherData = true
          }

          if (hasFresherData) {
            dispatch(setJackpotsData([...jackpotsByKey.values()]))
          }

          break
        }

        case 'BingoRoomsResponse': {
          dispatch(saveBingoRoomsJackpots((body as BingoRoomsResponseBody).bingoRooms))
          break
        }

        default: {
          if (process.env.PROFILE !== 'prod') {
            console.log(`%c${`Unhandled notify event - ${body}`}`, 'color:Orange', body)
          }
          break
        }
      }
    })
  }

export default wsNotifyHandler
